import React, { useState, useEffect, useRef } from 'react';
import { Users, Package, Clock, CheckCircle, AlertCircle, Barcode, Weight, X, Scan, Image } from 'lucide-react';
import { API_URL } from '../../services/api';

function SuiviOuvriersProduction({ isFinJournee = false }) {
  const [ouvriers, setOuvriers] = useState([]);
  const [ordresDisponibles, setOrdresDisponibles] = useState([]);
  const [selectedOuvrier, setSelectedOuvrier] = useState(null);
  const [showAffectationModal, setShowAffectationModal] = useState(false);
  const [showDetailOuvrierModal, setShowDetailOuvrierModal] = useState(false);
  const [showTerminerModal, setShowTerminerModal] = useState(false);
  const [ordreEnCours, setOrdreEnCours] = useState(null);
  const [formAffectation, setFormAffectation] = useState({
    ordre_id: '',
    poids_or_donne: '',
    search_query: '',
    nom_etape: '',
    etape_id: null,       // Patch 47 : etape_id requis (refonte semantique R2)
    piece_id: null,       // Pièce individuelle sélectionnée (null = OF complet)
  });
  const [filteredOrdres, setFilteredOrdres] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [stockOuvrierActuel, setStockOuvrierActuel] = useState(0);
  const [piecesOrdreSelectionne, setPiecesOrdreSelectionne] = useState([]); // Pièces de l'OF sélectionné
  const [loadingPieces, setLoadingPieces] = useState(false);
  const [formTerminer, setFormTerminer] = useState({
    poids_or_utilise: '',
    temps_minutes: '',
    poids_bijou_etape: '',
    notes: ''
  });
  const [etapesDevisOrdre, setEtapesDevisOrdre] = useState({ devis: [], autres: [] });
  const [dernierPoidsBijou, setDernierPoidsBijou] = useState(null);
  const [poidsOrDevis, setPoidsOrDevis] = useState(null);       // Or prévu dans le devis
  const [dernierOrRendu, setDernierOrRendu] = useState(null);   // Or rendu par l'ouvrier précédent
  const [scanValue, setScanValue] = useState('');
  const [scanMatch, setScanMatch] = useState(null);
  // V2.X-quater : onglet filtre taches (EN_COURS par defaut, HISTORIQUE pour terminees + duree)
  const [ongletTaches, setOngletTaches] = useState('EN_COURS');
  const scanInputRef = useRef(null);

  // ── Live timer ───────────────────────────────────────────────────
  const [now, setNow] = useState(Date.now());
  const loadedAtRef = useRef(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // V2.X-quater : formatDuree pour HISTORIQUE (date_debut -> date_fin)
  const formatDuree = (date_debut, date_fin) => {
    if (!date_debut || !date_fin) return '—';
    const d1 = new Date(date_debut).getTime();
    const d2 = new Date(date_fin).getTime();
    if (isNaN(d1) || isNaN(d2) || d2 < d1) return '—';
    const sec = Math.floor((d2 - d1) / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}min`;
    const h = Math.floor(min / 60);
    const mr = min % 60;
    if (h < 24) return mr > 0 ? `${h}h${String(mr).padStart(2,'0')}` : `${h}h`;
    const jours = Math.floor(h / 24);
    const hr = h % 24;
    if (jours < 7) return hr > 0 ? `${jours}j ${hr}h` : `${jours}j`;
    return `${jours} jours`;
  };

  const formatElapsed = (etape) => {
    if (etape.statut !== 'en_cours' && etape.statut !== 'pause') return null;
    // elapsed_seconds calculé serveur + incrément client (évite le problème de timezone)
    const base = etape.elapsed_seconds != null ? parseInt(etape.elapsed_seconds) : 0;
    const loadedAt = etape._loadedAt || loadedAtRef.current;
    const extraSec = etape.statut === 'en_cours' ? Math.max(0, Math.floor((now - loadedAt) / 1000)) : 0;
    const totalSec = Math.max(0, base + extraSec);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h${String(m).padStart(2,'0')}`;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };
  // ────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [ouvriersRes, ordresRes] = await Promise.all([
        fetch(`${API_URL}/atelier/ouvriers-production`),
        fetch(`${API_URL}/atelier/ordres-disponibles`)
      ]);
      
      if (ouvriersRes.ok) {
        const ouvriersData = await ouvriersRes.json();
        const loadedAt = Date.now();
        loadedAtRef.current = loadedAt;
        // Marquer le timestamp de chargement sur chaque étape pour le timer incrémental
        // Couvre toutes les structures possibles: ouvrier.etapes, ouvrier.ordres[].etapes
        const stampEtape = (e) => { if (e && typeof e === 'object') e._loadedAt = loadedAt; };
        (ouvriersData || []).forEach(o => {
          (o.etapes || []).forEach(stampEtape);
          (o.ordres || []).forEach(ord => {
            (ord.etapes || []).forEach(stampEtape);
          });
        });
        setOuvriers(ouvriersData);
        // Refresh selectedOuvrier si un ouvrier est ouvert
        setSelectedOuvrier(prev => {
          if (!prev) return prev;
          const updated = ouvriersData.find(o => o.id === prev.id);
          return updated || prev;
        });
      }
      
      if (ordresRes.ok) {
        const ordresData = await ordresRes.json();
        setOrdresDisponibles(ordresData);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const handleSearchChange = (value) => {
    setFormAffectation({...formAffectation, search_query: value});
    
    if (value.trim() === '') {
      setFilteredOrdres([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = ordresDisponibles.filter(ordre => 
      ordre.numero_ordre.toLowerCase().includes(value.toLowerCase()) ||
      ordre.type_bijou.toLowerCase().includes(value.toLowerCase()) ||
      (ordre.description && ordre.description.toLowerCase().includes(value.toLowerCase())) ||
      (ordre.client_nom && ordre.client_nom.toLowerCase().includes(value.toLowerCase())) ||
      (ordre.notes && ordre.notes.toLowerCase().includes(value.toLowerCase()))
    );
    
    setFilteredOrdres(filtered);
    setShowSuggestions(true);
  };

  const loadStockOuvrier = async (ouvrierId) => {
    try {
      const response = await fetch(`${API_URL}/atelier/stock-ouvrier/${ouvrierId}`);
      if (response.ok) {
        const data = await response.json();
        setStockOuvrierActuel(data.stock_or);
      }
    } catch (error) {
      console.error('Erreur chargement stock ouvrier:', error);
    }
  };

  const selectOrdre = async (ordre) => {
    setFormAffectation({...formAffectation, ordre_id: ordre.id, _parent_ordre_id: ordre.id, search_query: `${ordre.numero_ordre} ${ordre.type_bijou}`, nom_etape: '', etape_id: null, piece_id: null});
    setShowSuggestions(false);
    setPiecesOrdreSelectionne([]);

    // Récupérer les étapes + les pièces en parallèle
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };

    try {
      const [etapesRes, enfantsRes] = await Promise.all([
        fetch(`${API_URL}/atelier/fabrication/${ordre.id}/etapes`),
        parseInt(ordre.quantite_pieces || 1) > 1
          ? fetch(`${API_URL}/atelier/ordres-fabrication/${ordre.id}/enfants`, { headers: h })
          : Promise.resolve(null),
      ]);

      if (etapesRes.ok) {
        const data = await etapesRes.json();
        const toutes = data.etapes_devis || [];
        setEtapesDevisOrdre({
          devis: toutes.filter(e => e.in_devis),
          autres: toutes.filter(e => !e.in_devis)
        });
        // Pré-remplir le poids bijou : dernier poids mesuré, sinon poids_unitaire du devis
        const dpb = data.dernier_poids_bijou ?? data.poids_unitaire_of ?? null;
        setDernierPoidsBijou(dpb);
        setPoidsOrDevis(data.poids_or_devis ?? null);
        setDernierOrRendu(data.dernier_or_rendu ?? null);
      }

      if (enfantsRes && enfantsRes.ok) {
        const eData = await enfantsRes.json();
        // Normaliser les child OFs pour l'affichage (compatibilité avec reference_piece / ouvrier_nom)
        const enfants = (eData.enfants || []).map(e => ({
          ...e,
          reference_piece: e.numero_ordre,
          ouvrier_nom: e.artisan_nom || null,
        }));
        setPiecesOrdreSelectionne(enfants);
      }
    } catch (error) {
      console.error('Erreur sélection ordre:', error);
      setEtapesDevisOrdre({ devis: [], autres: [] });
    }
  };

  const affecterOrdre = async () => {
    if (!formAffectation.ordre_id) {
      alert('Veuillez sélectionner un ordre');
      return;
    }

    // Patch 47 : etape_id requis (refonte semantique R2)
    if (!formAffectation.etape_id) {
      alert("Veuillez selectionner une etape existante de l'OF");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      // Patch 47 : route legacy R2 (transitoire) gere encore la branche or
      // Quand le chantier "Stock or" sera fait, basculer sur POST /etapes/<id>/affecter pur
      const response = await fetch(`${API_URL}/atelier/affecter-etape-ouvrier`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ouvrier_id: selectedOuvrier.id,
          etape_id: formAffectation.etape_id,
          ordre_id: formAffectation.ordre_id,
          poids_or_donne: parseFloat(formAffectation.poids_or_donne) || 0,
          piece_id: formAffectation.piece_id || null,
        })
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        alert('Etape affectee avec succes !');
        setShowAffectationModal(false);
        setFormAffectation({ ordre_id: '', poids_or_donne: '', search_query: '', nom_etape: '', etape_id: null, piece_id: null });
        loadData();
      } else {
        let msg = data.error || data.message || 'Erreur affectation';
        if (response.status === 409) msg = `Etape deja prise par un autre ouvrier (statut: ${data.statut || '?'})`;
        else if (response.status === 404) msg = 'Etape introuvable';
        else if (response.status === 422) msg = "Cet OF n'a pas d'etapes definies. Creez les etapes avant d'affecter.";
        else if (response.status >= 500) msg = 'Erreur serveur, reessayez';
        alert(msg);
      }
    } catch (error) {
      console.error('Erreur affectation:', error);
      alert('Erreur lors de l\'affectation');
    }
  };

  const pauserOuvrier = async (ouvrierIdToPause) => {
    try {
      const res = await fetch(`${API_URL}/atelier/ouvriers-production/${ouvrierIdToPause}/pause`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        loadData();
      } else {
        alert(`Erreur: ${data.error}`);
      }
    } catch (e) {
      alert('Erreur lors de la mise en pause');
    }
  };

  const reprendreOuvrier = async (ouvrierIdToResume) => {
    try {
      const res = await fetch(`${API_URL}/atelier/ouvriers-production/${ouvrierIdToResume}/reprendre`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        loadData();
      } else {
        alert(`Erreur: ${data.error}`);
      }
    } catch (e) {
      alert('Erreur lors de la reprise');
    }
  };

  const pauserEtapeOuvrier = async (etapeId) => {
    try {
      const res = await fetch(`${API_URL}/atelier/etapes/${etapeId}/pause`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) { await loadData(); }
      else { const d = await res.json(); alert(d.error || 'Erreur pause'); }
    } catch { alert('Erreur réseau'); }
  };

  const reprendreEtapeOuvrier = async (etapeId) => {
    try {
      const res = await fetch(`${API_URL}/atelier/etapes/${etapeId}/reprendre`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) { await loadData(); }
      else { const d = await res.json(); alert(d.error || 'Erreur reprise'); }
    } catch { alert('Erreur réseau'); }
  };

  // Étapes qui nécessitent de l'or
  const ETAPES_AVEC_OR = [/fontes?/i, /sertis+age/i, /montage/i, /assemblage/i, /soudure/i, /gravure/i, /polissage/i, /emaillage/i, /rhodiage/i];
  const etapeNecessiteOr = (nom) => ETAPES_AVEC_OR.some(r => r.test(nom || ''));

  const terminerTache = async () => {
    // Poids or obligatoire seulement pour les étapes métallurgiques
    const needsOr = etapeNecessiteOr(ordreEnCours?.mission);
    if (needsOr && !formTerminer.poids_or_utilise) {
      alert('Veuillez entrer le poids d\'or utilise pour cette etape');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/atelier/terminer-tache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affectation_id: ordreEnCours.affectation_id,
          ouvrier_id: selectedOuvrier.id,
          poids_or_utilise: parseFloat(formTerminer.poids_or_utilise),
          temps_minutes: formTerminer.temps_minutes ? parseInt(formTerminer.temps_minutes) : null,
          poids_bijou_etape: formTerminer.poids_bijou_etape ? parseFloat(formTerminer.poids_bijou_etape) : null,
          rendre_or: formTerminer.rendre_or || false,
          notes: formTerminer.notes
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Tâche terminée ! Or restant: ${result.or_restant}g ajouté à la caisse`);
        setShowTerminerModal(false);
        setFormTerminer({ poids_or_utilise: '', temps_minutes: '', poids_bijou_etape: '', notes: '' });
        setShowDetailOuvrierModal(false);
        loadData();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Erreur terminer tâche:', error);
      alert('Erreur lors de la finalisation');
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="text-blue-600" />
            Suivi Ouvriers - Production
          </h1>
          <p className="text-gray-600 mt-1">Affectation et suivi des ordres de fabrication</p>
        </div>
      </div>

      {/* Liste des ouvriers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ouvriers.map(ouvrier => (
          <div
            key={ouvrier.id}
            className="bg-white rounded-lg shadow-md p-4 border-2 border-gray-200 hover:border-blue-400 cursor-pointer transition-all"
            onClick={() => {
              setSelectedOuvrier(ouvrier);
              if (ouvrier.ordres_en_cours > 0) {
                setShowDetailOuvrierModal(true);
              }
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {ouvrier.photo_url ? (
                  <img
                    src={ouvrier.photo_url.startsWith('http') ? ouvrier.photo_url : `${API_URL.replace('/api', '')}${ouvrier.photo_url}`}
                    alt={ouvrier.nom}
                    className="w-10 h-10 rounded-full object-cover border-2 border-blue-200"
                  />
                ) : (
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-600 text-sm">
                    {ouvrier.prenom?.charAt(0)}{ouvrier.nom?.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="font-bold">{ouvrier.nom} {ouvrier.prenom}</h3>
                  <p className="text-xs text-gray-500">{ouvrier.specialite || 'Ouvrier'}</p>
                </div>
              </div>
              {ouvrier.ordres_en_cours > 0 ? (
                <span className="bg-blue-100 text-blue-700 px-3 py-2 rounded-full text-sm font-bold">
                  🔵 {ouvrier.ordres_en_cours} {ouvrier.ordres_en_cours > 1 ? 'tâches' : 'tâche'}
                </span>
              ) : ouvrier.ordres_en_pause > 0 ? (
                <span className="bg-yellow-100 text-yellow-800 px-3 py-2 rounded-full text-sm font-bold">
                  🌙 Ouvrier en pause
                </span>
              ) : (
                <span className="bg-green-100 text-green-700 px-3 py-2 rounded-full text-sm font-bold">
                  Disponible
                </span>
              )}
            </div>

            <div className="space-y-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOuvrier(ouvrier);
                  loadStockOuvrier(ouvrier.id);
                  setShowAffectationModal(true);
                }}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm font-semibold"
              >
                Affecter une fiche
              </button>

              {ouvrier.ordres_en_cours > 0 && !isFinJournee && (
                <button
                  onClick={(e) => { e.stopPropagation(); pauserOuvrier(ouvrier.id); }}
                  className="w-full bg-yellow-500 text-white py-2 rounded hover:bg-yellow-600 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <span>⏸</span> Pause toutes ses tâches
                </button>
              )}

              {ouvrier.ordres_en_pause > 0 && ouvrier.ordres_en_cours === 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); reprendreOuvrier(ouvrier.id); }}
                  className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <span>▶</span> Reprendre ses tâches
                </button>
              )}

              {(ouvrier.ordres_en_cours > 0 || ouvrier.ordres_en_pause > 0) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOuvrier(ouvrier);
                    setShowDetailOuvrierModal(true);
                  }}
                  className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 text-sm font-semibold"
                >
                  Terminer une tâche
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal Détail Ouvrier */}
      {showDetailOuvrierModal && selectedOuvrier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 w-full max-w-2xl max-h-[95vh] sm:max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                Tâches de {selectedOuvrier.nom} {selectedOuvrier.prenom}
              </h2>
              <button
                onClick={() => { setShowDetailOuvrierModal(false); setScanValue(''); setScanMatch(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Champ scan ordre */}
            <div className="mb-4">
              <div className="flex items-center gap-2 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl px-3 py-2 focus-within:border-blue-400 focus-within:bg-blue-50 transition-colors">
                <Scan size={18} className="text-gray-400 flex-shrink-0" />
                <input
                  ref={scanInputRef}
                  type="text"
                  value={scanValue}
                  onChange={e => {
                    const val = e.target.value;
                    setScanValue(val);
                    if (val.length >= 3 && selectedOuvrier.ordres) {
                      const found = selectedOuvrier.ordres.find(o =>
                        (o.numero_ordre || '').toLowerCase().includes(val.toLowerCase()) ||
                        (o.code_barre || '').toLowerCase().includes(val.toLowerCase())
                      );
                      setScanMatch(found ? found.affectation_id : 'none');
                    } else {
                      setScanMatch(null);
                    }
                  }}
                  placeholder="Scanner le code-barre de l'ordre (ex: OF000069001)..."
                  className="flex-1 bg-transparent outline-none text-sm"
                  autoComplete="off"
                />
                {scanValue && (
                  <button onClick={() => { setScanValue(''); setScanMatch(null); }}
                    className="text-gray-400 hover:text-gray-600">
                    <X size={14} />
                  </button>
                )}
              </div>
              {scanMatch === 'none' && scanValue && (
                <p className="text-xs text-red-500 mt-1 ml-2">⚠ Aucun ordre trouvé pour ce code</p>
              )}
            </div>

            {/* V2.X-quater : Onglets EN COURS / HISTORIQUE */}
            {(() => {
              const allOrdres = selectedOuvrier.ordres || [];
              const PRIO_RANK = { 'urgente': 4, 'haute': 3, 'normale': 2, 'basse': 1 };
              const enCours = allOrdres
                .filter(o => o.statut !== 'termine')
                .sort((a, b) => {
                  const pa = PRIO_RANK[a.priorite] || 0;
                  const pb = PRIO_RANK[b.priorite] || 0;
                  if (pa !== pb) return pb - pa;  // priorite DESC
                  return new Date(a.date_debut || 0) - new Date(b.date_debut || 0);  // age ASC
                });
              const historique = allOrdres
                .filter(o => o.statut === 'termine')
                .sort((a, b) => new Date(b.date_fin || 0) - new Date(a.date_fin || 0));
              return (
                <div className="flex gap-2 mb-3 border-b border-gray-200">
                  <button
                    onClick={() => setOngletTaches('EN_COURS')}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                      ongletTaches === 'EN_COURS'
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    🔵 EN COURS ({enCours.length})
                  </button>
                  <button
                    onClick={() => setOngletTaches('HISTORIQUE')}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                      ongletTaches === 'HISTORIQUE'
                        ? 'border-green-600 text-green-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    ✓ HISTORIQUE ({historique.length})
                  </button>
                </div>
              );
            })()}

            <div className="space-y-3">
              {(() => {
                const allOrdres = selectedOuvrier.ordres || [];
                const PRIO_RANK = { 'urgente': 4, 'haute': 3, 'normale': 2, 'basse': 1 };
                const ordresFiltered = ongletTaches === 'EN_COURS'
                  ? allOrdres
                      .filter(o => o.statut !== 'termine')
                      .sort((a, b) => {
                        const pa = PRIO_RANK[a.priorite] || 0;
                        const pb = PRIO_RANK[b.priorite] || 0;
                        if (pa !== pb) return pb - pa;
                        return new Date(a.date_debut || 0) - new Date(b.date_debut || 0);
                      })
                  : allOrdres
                      .filter(o => o.statut === 'termine')
                      .sort((a, b) => new Date(b.date_fin || 0) - new Date(a.date_fin || 0));
                return ordresFiltered.length > 0 ? (
                  ordresFiltered.map(ordre => (
                  <div key={ordre.affectation_id} className={`rounded-lg p-4 border-2 transition-all ${
                    scanMatch && scanMatch !== 'none' && scanMatch === ordre.affectation_id
                      ? 'ring-4 ring-yellow-400 ring-offset-2'
                      : ''
                  } ${
                    ordre.statut === 'termine'
                      ? 'bg-green-50 border-green-300'
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Barcode size={18} className={ordre.statut === 'termine' ? 'text-green-600' : 'text-blue-600'} />
                        <span className="font-bold text-gray-900">{ordre.numero_ordre}</span>
                        {ordre.code_barre && (
                          <span className="text-xs text-gray-400 font-mono">{ordre.code_barre}</span>
                        )}
                        {/* V2.X-quater : badge priorite pour EN COURS */}
                        {ordre.statut !== 'termine' && ordre.priorite && ordre.priorite !== 'normale' && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            ordre.priorite === 'urgente' ? 'bg-red-600 text-white' :
                            ordre.priorite === 'haute' ? 'bg-orange-500 text-white' :
                            'bg-gray-300 text-gray-700'
                          }`}>
                            {ordre.priorite === 'urgente' ? '🔥 URGENT' :
                             ordre.priorite === 'haute' ? '⬆ Haute' :
                             '⬇ Basse'}
                          </span>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        ordre.statut === 'termine'
                          ? 'bg-green-600 text-white'
                          : ordre.statut === 'pause'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-blue-600 text-white'
                      }`}>
                        {ordre.statut === 'termine' ? '✓ Terminé' : ordre.statut === 'pause' ? '⏸ Pause' : '⚙️ En cours'}
                      </span>
                    </div>

                    {/* Photo + Détails du bijou */}
                    <div className="bg-white bg-opacity-60 rounded-lg p-3 mb-3 flex gap-3">
                      {/* Photo bijou */}
                      {ordre.image_url ? (
                        <img
                          src={ordre.image_url.startsWith('http') ? ordre.image_url : `${API_URL.replace('/api', '')}${ordre.image_url}`}
                          alt={ordre.type_bijou}
                          className="w-20 h-20 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                          <Image size={24} className="text-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-purple-700 mb-1">
                          📋 Mission : {ordre.mission || 'Non spécifiée'}
                        </div>
                        <div className="text-sm text-gray-700">
                          <div className="font-medium">{ordre.type_bijou}</div>
                          {ordre.description && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{ordre.description}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Informations sur l'or */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-white bg-opacity-60 rounded p-2 text-center">
                        <div className="text-xs text-gray-600">Or donné</div>
                        <div className="font-bold text-amber-600">
                          {parseFloat(ordre.poids_or_donne).toFixed(2)}g
                        </div>
                      </div>
                      {ordre.statut === 'termine' && (
                        <>
                          <div className="bg-white bg-opacity-60 rounded p-2 text-center">
                            <div className="text-xs text-gray-600">Utilisé</div>
                            <div className="font-bold text-green-600">
                              {ordre.poids_bijou_fini ? parseFloat(ordre.poids_bijou_fini).toFixed(2) : '0.00'}g
                            </div>
                          </div>
                          <div className="bg-white bg-opacity-60 rounded p-2 text-center">
                            <div className="text-xs text-gray-600">Restant</div>
                            <div className="font-bold text-blue-600">
                              {ordre.or_restant ? parseFloat(ordre.or_restant).toFixed(2) : '0.00'}g
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Dates */}
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        <span>Début: {new Date(ordre.date_debut).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {ordre.statut === 'termine' && ordre.date_fin && (
                        <>
                          <div className="flex items-center gap-1">
                            <CheckCircle size={12} />
                            <span>Fin: {new Date(ordre.date_fin).toLocaleDateString('fr-FR')}</span>
                          </div>
                          <div className="flex items-center gap-1 font-semibold text-green-700">
                            ⏱ {formatDuree(ordre.date_debut, ordre.date_fin)}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Notes */}
                    {ordre.notes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-3">
                        <div className="text-xs font-semibold text-yellow-800">Notes:</div>
                        <div className="text-xs text-gray-700">{ordre.notes}</div>
                      </div>
                    )}

                    {/* Étapes assignées à cet ouvrier */}
                    {ordre.etapes && ordre.etapes.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Étapes</div>
                        {ordre.etapes.map(etape => (
                          <div key={etape.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${
                            etape.statut === 'terminee' ? 'bg-green-50 border-green-200' :
                            etape.statut === 'pause' ? 'bg-yellow-50 border-yellow-300' :
                            'bg-blue-50 border-blue-200'
                          }`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs">{
                                etape.statut === 'terminee' ? '✅' :
                                etape.statut === 'pause' ? '⏸️' : '🔵'
                              }</span>
                              <span className="text-sm font-medium text-gray-800 truncate">{etape.nom_etape}</span>
                              <span className={`text-xs ml-1 ${etape.type_ressource === 'interne' ? 'text-blue-500' : 'text-orange-500'}`}>
                                {etape.type_ressource === 'interne' ? '🏭' : '🔗'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* Live timer */}
                              {(() => {
                                const t = formatElapsed(etape);
                                if (!t) return null;
                                return (
                                  <span className={`text-xs font-mono font-semibold tabular-nums ${etape.statut === 'pause' ? 'text-yellow-600' : 'text-blue-600'}`}>
                                    {etape.statut === 'pause' ? '⏸ ' : '⏱ '}{t}
                                  </span>
                                );
                              })()}
                              {etape.statut === 'en_cours' && (
                                <button
                                  onClick={() => pauserEtapeOuvrier(etape.id)}
                                  className="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 font-semibold"
                                >⏸ Pause</button>
                              )}
                              {etape.statut === 'pause' && (
                                <button
                                  onClick={() => reprendreEtapeOuvrier(etape.id)}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-semibold"
                                >▶ Reprendre</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions par OF */}
                    {ordre.statut !== 'termine' && (
                      <div className="mt-3 flex gap-2">
                        {ordre.statut === 'en_cours' && (
                          <button
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              await fetch(`${API_URL}/atelier/ouvriers/${selectedOuvrier.id}/ordres/${ordre.ordre_id}/pause`, {
                                method: 'POST', headers: { Authorization: `Bearer ${token}` }
                              });
                              loadData();
                            }}
                            className="flex-1 bg-yellow-500 text-white py-2 rounded hover:bg-yellow-600 text-sm font-semibold flex items-center justify-center gap-1"
                          >⏸ Pause cet OF</button>
                        )}
                        {ordre.statut === 'pause' && (
                          <button
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              await fetch(`${API_URL}/atelier/ouvriers/${selectedOuvrier.id}/ordres/${ordre.ordre_id}/reprendre`, {
                                method: 'POST', headers: { Authorization: `Bearer ${token}` }
                              });
                              loadData();
                            }}
                            className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm font-semibold flex items-center justify-center gap-1"
                          >▶ Reprendre cet OF</button>
                        )}
                        <button
                          onClick={async () => {
                            setOrdreEnCours(ordre);
                            // §73 - prefill via preview-poids-or si etape_id dispo
                            let suggested = null;
                            let origine = null;
                            if (ordre.etape_id) {
                              try {
                                const res = await fetch(`${API_URL}/atelier/etapes/${ordre.etape_id}/preview-poids-or`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                if (res.ok) {
                                  const d = await res.json();
                                  // Priorite : valeur existante saisie par chef > suggestion calculee
                                  suggested = d.poids_or_donne_existant
                                            ?? d.poids_or_donne_suggested
                                            ?? d.poids_or_recu_suggested
                                            ?? null;
                                  origine = d.poids_or_donne_existant != null
                                    ? 'saisi par chef'
                                    : (d.origine_label || null);
                                  ordre.poids_or_donne_suggested = suggested;
                                  ordre.poids_or_origine = origine;
                                }
                              } catch { /* silent */ }
                            }
                            // Fallback : utiliser dernier_or_recu_apres OR dernier_poids_bijou OR of_poids_or_estime
                            const fallback = ordre.dernier_or_recu_apres
                                          ?? ordre.dernier_poids_bijou
                                          ?? ordre.of_poids_or_estime
                                          ?? ordre.poids_or_donne;
                            const prefillVal = suggested ?? fallback;
                            // §97 - Prefill temps_minutes depuis timer live (elapsed_seconds + extraSec client)
                            let tempsPrefill = '';
                            try {
                              const activeEtape = (ordre.etapes || []).find(e => e.id === ordre.etape_id)
                                                 || (ordre.etapes || []).find(e => e.statut === 'en_cours' || e.statut === 'pause');
                              if (activeEtape) {
                                const base = activeEtape.elapsed_seconds != null ? parseInt(activeEtape.elapsed_seconds) : 0;
                                const loadedAt = activeEtape._loadedAt || loadedAtRef.current;
                                const extraSec = activeEtape.statut === 'en_cours'
                                  ? Math.max(0, Math.floor((Date.now() - loadedAt) / 1000))
                                  : 0;
                                const totalSec = Math.max(0, base + extraSec);
                                const mins = Math.round(totalSec / 60);
                                if (mins > 0) tempsPrefill = String(mins);
                              }
                            } catch { /* silent */ }
                            setFormTerminer(f => ({
                              ...f,
                              poids_or_utilise: prefillVal != null
                                ? String(parseFloat(prefillVal).toFixed(3))
                                : '',
                              poids_bijou_etape: ordre.dernier_poids_bijou != null
                                ? String(parseFloat(ordre.dernier_poids_bijou).toFixed(2))
                                : '',
                              temps_minutes: tempsPrefill,
                            }));
                            setShowTerminerModal(true);
                          }}
                          className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 text-sm font-semibold flex items-center justify-center gap-2"
                        >
                          <CheckCircle size={16} />
                          Terminer
                        </button>
                      </div>
                    )}
                  </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Clock size={48} className="mx-auto mb-2" />
                    <p>{ongletTaches === 'EN_COURS' ? 'Aucune tache en cours' : 'Aucun historique'}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal Affectation — layout 2 colonnes */}
      {showAffectationModal && selectedOuvrier && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col w-full max-h-[95vh] sm:max-h-[88vh]" style={{maxWidth: '860px'}}>

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between shrink-0">
              <div>
                <div className="font-bold text-xl">{selectedOuvrier.prenom} {selectedOuvrier.nom}</div>
                <div className="text-blue-200 text-sm">Nouvelle affectation de tâche</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-yellow-400 text-yellow-900 font-bold px-4 py-2 rounded-xl text-sm">
                  Or disponible : {stockOuvrierActuel}g
                </div>
                <button onClick={() => {
                  setShowAffectationModal(false);
                  setPiecesOrdreSelectionne([]);
                  setFormAffectation({ ordre_id: '', poids_or_donne: '', search_query: '', nom_etape: '', etape_id: null, piece_id: null, _parent_ordre_id: null });
                }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Corps 2 colonnes */}
            <div className="flex flex-col sm:flex-row flex-1 min-h-0 sm:divide-x divide-gray-200 overflow-y-auto">

              {/* ── Colonne gauche : sélection de l'ordre ── */}
              <div className="sm:w-2/5 flex flex-col min-h-0 p-4 border-b sm:border-b-0">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Fiche de fabrication
                </div>
                <input
                  type="text"
                  value={formAffectation.search_query}
                  onChange={(e) => {
                    handleSearchChange(e.target.value);
                    if (!e.target.value) setFormAffectation({...formAffectation, search_query: '', ordre_id: '', nom_etape: ''});
                  }}
                  className="w-full border-2 border-gray-200 focus:border-blue-400 rounded-xl px-3 py-2.5 text-sm outline-none mb-3"
                  placeholder="Rechercher..."
                />
                <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0">
                  {(formAffectation.search_query ? filteredOrdres : ordresDisponibles.filter(o => o.statut !== 'termine')).map(ordre => (
                    <div
                      key={ordre.id}
                      onClick={() => selectOrdre(ordre)}
                      className={`rounded-xl px-3 py-2.5 cursor-pointer transition-all border-2 ${
                        formAffectation.ordre_id === String(ordre.id)
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                          : 'bg-gray-50 hover:bg-blue-50 border-transparent hover:border-blue-200'
                      }`}
                    >
                      <div className={`font-bold text-sm ${formAffectation.ordre_id === String(ordre.id) ? 'text-white' : 'text-blue-700'}`}>
                        {ordre.numero_ordre}
                        {parseInt(ordre.quantite_pieces || 1) > 1 && (
                          <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded-full ${formAffectation.ordre_id === String(ordre.id) ? 'bg-blue-400 text-white' : 'bg-blue-100 text-blue-600'}`}>
                            x{ordre.quantite_pieces}
                          </span>
                        )}
                      </div>
                      <div className={`text-xs mt-0.5 ${formAffectation.ordre_id === String(ordre.id) ? 'text-blue-100' : 'text-gray-500'}`}>
                        {ordre.type_bijou}{ordre.client_nom ? ` · ${ordre.client_nom}` : ''}
                      </div>
                      {ordre.description && (
                        <div className={`text-xs mt-0.5 truncate ${formAffectation.ordre_id === String(ordre.id) ? 'text-blue-200' : 'text-gray-400'}`}>
                          {parseInt(ordre.quantite_pieces || 1) > 1
                            ? `Production ${ordre.type_bijou} (x${ordre.quantite_pieces})`
                            : ordre.description.slice(0, 40)}
                        </div>
                      )}
                    </div>
                  ))}
                  {ordresDisponibles.filter(o => o.statut !== 'termine').length === 0 && (
                    <div className="text-center text-gray-400 text-sm py-8">Aucun ordre en cours</div>
                  )}
                </div>
              </div>

              {/* ── Colonne droite : pièces + étapes + or ── */}
              <div className="flex-1 flex flex-col min-h-0 sm:overflow-hidden">
                {!formAffectation.ordre_id ? (
                  <div className="flex-1 flex items-center justify-center text-gray-300 text-sm">
                    Sélectionnez un ordre à gauche
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto p-4 min-h-0">

                      {/* ── Sélecteur de pièce (si OF multi-bijoux) ── */}
                      {piecesOrdreSelectionne.length > 1 && (
                        <div className="mb-4 pb-4 border-b border-gray-200">
                          <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <span>🔹</span> Pièce à affecter ({piecesOrdreSelectionne.length} bijoux dans cet OF)
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {piecesOrdreSelectionne.map(piece => {
                              const isSelected = formAffectation.piece_id === piece.id;
                              const statutColor = piece.statut === 'terminee' ? 'bg-green-100 text-green-700 border-green-300'
                                : piece.statut === 'en_cours' ? 'bg-blue-100 text-blue-700 border-blue-300'
                                : piece.statut === 'assignee' ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                                : 'bg-gray-50 text-gray-700 border-gray-200';
                              return (
                                <button
                                  key={piece.id}
                                  type="button"
                                  onClick={async () => {
                                    const sel = isSelected ? null : piece.id;
                                    const newOrdreId = sel ? piece.id : formAffectation._parent_ordre_id || formAffectation.ordre_id;
                                    setFormAffectation(f => ({
                                      ...f,
                                      piece_id: sel,
                                      ordre_id: newOrdreId,
                                      nom_etape: '',
                                      etape_id: null,
                                      _parent_ordre_id: f._parent_ordre_id || f.ordre_id,
                                    }));
                                    // Charger les étapes du child OF sélectionné
                                    if (sel) {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const res = await fetch(`${API_URL}/atelier/fabrication/${sel}/etapes`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        if (res.ok) {
                                          const data = await res.json();
                                          const toutes = data.etapes_devis || [];
                                          setEtapesDevisOrdre({
                                            devis: toutes.filter(e => e.in_devis),
                                            autres: toutes.filter(e => !e.in_devis),
                                          });
                                          const dpb = data.dernier_poids_bijou ?? data.poids_unitaire_of ?? null;
                                          setDernierPoidsBijou(dpb);
                                          setPoidsOrDevis(data.poids_or_devis ?? null);
                                          setDernierOrRendu(data.dernier_or_rendu ?? null);
                                        }
                                      } catch (err) {
                                        console.error('Erreur chargement étapes piece:', err);
                                      }
                                    } else {
                                      // Désélection : vider les étapes (le parent n'en a pas)
                                      setEtapesDevisOrdre({ devis: [], autres: [] });
                                      setDernierPoidsBijou(null);
                                    }
                                  }}
                                  className={`rounded-xl px-3 py-2.5 text-left transition-all border-2 ${
                                    isSelected
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                      : statutColor + ' hover:border-blue-300'
                                  }`}
                                >
                                  <div className={`font-bold text-sm font-mono ${isSelected ? 'text-white' : ''}`}>
                                    {piece.reference_piece}
                                  </div>
                                  <div className={`text-xs mt-0.5 ${isSelected ? 'text-blue-200' : 'text-gray-500'}`}>
                                    {piece.couleur_or}
                                    {piece.ouvrier_nom ? ` · ${piece.ouvrier_nom}` : ''}
                                  </div>
                                  <div className={`text-xs mt-0.5 ${isSelected ? 'text-blue-200' : 'text-gray-400'}`}>
                                    {piece.statut === 'terminee' ? '✓ Terminée'
                                      : piece.statut === 'en_cours' ? '⟳ En cours'
                                      : piece.statut === 'assignee' ? '→ Assignée'
                                      : '○ En attente'}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {formAffectation.piece_id && (
                            <div className="mt-2 text-xs text-blue-700 font-semibold bg-blue-50 rounded-lg px-3 py-1.5 flex items-center justify-between">
                              <span>Pièce sélectionnée : {piecesOrdreSelectionne.find(p => p.id === formAffectation.piece_id)?.reference_piece}</span>
                              <button onClick={() => setFormAffectation(f => ({ ...f, piece_id: null, ordre_id: f._parent_ordre_id || f.ordre_id }))} className="text-blue-400 hover:text-blue-600 ml-2">✕</button>
                            </div>
                          )}
                          {!formAffectation.piece_id && (
                            <div className="mt-2 text-xs text-gray-400 italic">
                              Sélectionner une pièce ci-dessus, ou laisser vide pour affecter à l'OF entier
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                        Étape à affecter
                      </div>

                      {etapesDevisOrdre.devis.length > 0 && (() => {
                        const nonFaites = etapesDevisOrdre.devis.filter(e => e.statut !== 'terminee');
                        const faites    = etapesDevisOrdre.devis.filter(e => e.statut === 'terminee');
                        return (
                          <>
                            {/* Étapes disponibles (non terminées) → VERT */}
                            {nonFaites.length > 0 && (
                              <div className="mb-4">
                                <div className="text-xs font-semibold text-green-700 mb-2">
                                  À faire — prévues dans le devis
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  {nonFaites.map((etape, i) => (
                                    <button key={i} type="button"
                                      onClick={() => setFormAffectation({...formAffectation, nom_etape: etape.nom_etape, etape_id: etape.id})}
                                      className={`px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-all border-2 ${
                                        formAffectation.etape_id === etape.id
                                          ? etape.type_ressource === 'externe'
                                            ? 'bg-orange-500 text-white border-orange-500 shadow'
                                            : 'bg-green-500 text-white border-green-500 shadow'
                                          : etape.type_ressource === 'externe'
                                          ? 'bg-orange-50 text-orange-800 border-orange-300 hover:border-orange-500'
                                          : etape.statut === 'en_cours'
                                          ? 'bg-blue-50 text-blue-800 border-blue-300 hover:border-blue-500'
                                          : 'bg-green-50 text-green-800 border-green-200 hover:border-green-400'
                                      }`}
                                    >
                                      {etape.type_ressource === 'externe' ? '🔗' : etape.statut === 'en_cours' ? '🔵' : '🟢'} {etape.nom_etape}
                                      {etape.type_ressource === 'externe' && (
                                        <span className="block text-xs font-normal opacity-75">Prestataire externe</span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Étapes déjà terminées → ORANGE en bas (refaisable) */}
                            {faites.length > 0 && (
                              <div className="mb-4">
                                <div className="text-xs font-semibold text-orange-600 mb-2">
                                  Déjà effectuées — cliquer pour réaffecter
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  {faites.map((etape, i) => (
                                    <button key={i} type="button"
                                      onClick={() => setFormAffectation({...formAffectation, nom_etape: etape.nom_etape, etape_id: etape.id})}
                                      className={`px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-all border-2 ${
                                        formAffectation.etape_id === etape.id
                                          ? 'bg-orange-500 text-white border-orange-500 shadow'
                                          : 'bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400'
                                      }`}
                                    >
                                      🟠 {etape.nom_etape}
                                      <span className={`ml-1 text-xs ${etape.type_ressource === 'interne' ? 'text-blue-400' : 'text-orange-400'}`}>
                                        {etape.type_ressource === 'interne' ? '🏭' : '🔗'}
                                      </span>
                                      <span className="block text-xs font-normal opacity-70">✓ déjà fait</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {etapesDevisOrdre.autres.length > 0 && (
                        <>
                          {/* Étapes hors-devis déjà en production */}
                          {etapesDevisOrdre.autres.filter(e => !e.du_catalogue).length > 0 && (
                            <div className="mb-4">
                              <div className="text-xs font-semibold text-gray-500 mb-2">Ajoutées en production (hors devis)</div>
                              <div className="grid grid-cols-2 gap-2">
                                {etapesDevisOrdre.autres.filter(e => !e.du_catalogue).map((etape, i) => (
                                  <button key={i} type="button"
                                    onClick={() => setFormAffectation({...formAffectation, nom_etape: etape.nom_etape, etape_id: etape.id})}
                                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-all border-2 ${
                                      formAffectation.etape_id === etape.id
                                        ? etape.statut === 'terminee'
                                          ? 'bg-orange-500 text-white border-orange-500 shadow'
                                          : 'bg-gray-600 text-white border-gray-600 shadow'
                                        : etape.statut === 'terminee'
                                          ? 'bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400'
                                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400'
                                    }`}
                                  >
                                    {etape.statut === 'terminee' ? '🟠' : '⚪'} {etape.nom_etape}
                                    <span className={`ml-1 text-xs ${etape.type_ressource === 'interne' ? 'text-blue-400' : 'text-orange-400'}`}>
                                      {etape.type_ressource === 'interne' ? '🏭' : '🔗'}
                                    </span>
                                    {etape.statut === 'terminee' && <span className="block text-xs font-normal opacity-70">✓ déjà fait</span>}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Étapes du catalogue non encore utilisées */}
                          {etapesDevisOrdre.autres.filter(e => e.du_catalogue).length > 0 && (
                            <div className="mb-4">
                              <div className="text-xs font-semibold text-gray-400 mb-2">Autres étapes disponibles</div>
                              <div className="grid grid-cols-2 gap-2">
                                {etapesDevisOrdre.autres.filter(e => e.du_catalogue).map((etape, i) => (
                                  <button key={i} type="button"
                                    onClick={() => setFormAffectation({...formAffectation, nom_etape: etape.nom_etape, etape_id: etape.id})}
                                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-all border-2 ${
                                      formAffectation.etape_id === etape.id
                                        ? 'bg-blue-500 text-white border-blue-500 shadow'
                                        : 'bg-gray-50 text-gray-400 border-dashed border-gray-200 hover:border-blue-300 hover:text-blue-600'
                                    }`}
                                  >
                                    ○ {etape.nom_etape}
                                    <span className={`ml-1 text-xs ${etape.type_ressource === 'interne' ? 'text-blue-400' : 'text-orange-400'}`}>
                                      {etape.type_ressource === 'interne' ? '🏭' : '🔗'}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-1.5">Étape personnalisée</div>
                        <input type="text"
                          value={formAffectation.nom_etape}
                          onChange={(e) => setFormAffectation({...formAffectation, nom_etape: e.target.value})}
                          className="w-full border-2 border-gray-200 focus:border-blue-400 rounded-xl px-3 py-2.5 text-sm outline-none"
                          placeholder="Ex: Réparation, Retouche..."
                        />
                      </div>
                    </div>

                    {/* Footer avec or + boutons */}
                    <div className="border-t px-4 py-3 bg-gray-50 rounded-br-2xl shrink-0">
                      {formAffectation.nom_etape && (
                        <div className="mb-2 text-xs text-blue-700 font-semibold bg-blue-50 rounded-lg px-3 py-1.5">
                          Étape sélectionnée : {formAffectation.nom_etape}
                        </div>
                      )}
                      {/* Indicateurs or */}
                      <div className="flex gap-2 mb-3">
                        {poidsOrDevis !== null && (
                          <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center">
                            <div className="text-xs text-amber-600 font-medium">Or du devis</div>
                            <div className="text-sm font-bold text-amber-800">{parseFloat(poidsOrDevis).toFixed(2)}g</div>
                          </div>
                        )}
                        {dernierOrRendu !== null && (
                          <div className="flex-1 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-center">
                            <div className="text-xs text-green-600 font-medium">Rendu dernière étape</div>
                            <div className="text-sm font-bold text-green-800">{parseFloat(dernierOrRendu).toFixed(2)}g</div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs text-gray-500 whitespace-nowrap">Or donné (g) :</span>
                        <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01"
                          value={formAffectation.poids_or_donne}
                          onChange={(e) => setFormAffectation({...formAffectation, poids_or_donne: e.target.value})}
                          className="flex-1 border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-blue-400"
                          placeholder="0 (optionnel)"
                        />
                      </div>
                      {dernierPoidsBijou !== null && (
                        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200">
                          <Weight size={14} className="text-purple-600 flex-shrink-0" />
                          <span className="text-xs text-purple-700 font-semibold">
                            Poids bijou à la dernière étape :
                          </span>
                          <span className="text-sm font-bold text-purple-800 ml-auto">
                            {parseFloat(dernierPoidsBijou).toFixed(2)} g
                          </span>
                        </div>
                      )}
                      <button onClick={affecterOrdre}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 text-sm font-bold shadow transition-colors">
                        Affecter cette tâche
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Modal Terminer tâche */}
      {showTerminerModal && ordreEnCours && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 w-full max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Barcode className="text-blue-600" />
              {ordreEnCours.numero_ordre}
            </h2>

            <div className="bg-blue-50 p-4 rounded-lg mb-4 border-2 border-blue-200">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <strong>{ordreEnCours.type_bijou}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Description:</span>
                  <strong>{ordreEnCours.description}</strong>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Or donné:</span>
                  <strong className="text-orange-600 text-lg">
                    {(() => {
                      const v = ordreEnCours.poids_or_donne_suggested ?? ordreEnCours.poids_or_donne;
                      return v != null && parseFloat(v) > 0 ? `${parseFloat(v).toFixed(3)}g` : '—';
                    })()}
                  </strong>
                </div>
                {ordreEnCours.poids_or_origine && (
                  <div className="text-xs text-gray-500 italic text-right">
                    ({ordreEnCours.poids_or_origine})
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {/* Poids or — seulement pour les étapes métallurgiques */}
              {etapeNecessiteOr(ordreEnCours?.mission) ? (
                <div>
                  <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                    <Weight size={16} />
                    Poids d'or utilise pour le bijou (grammes)
                  </label>
                  <input
                    type="text"
                    value={formTerminer.poids_or_utilise}
                    onChange={(e) => setFormTerminer({...formTerminer, poids_or_utilise: e.target.value.replace(',', '.')})}
                    className="w-full border-2 rounded px-3 py-2 text-lg font-semibold"
                    placeholder="Ex: 8.50"
                  />
                  {formTerminer.poids_or_utilise && ordreEnCours.poids_or_donne && (
                    <div className="mt-2 p-3 bg-green-50 border-2 border-green-200 rounded">
                      <p className="text-sm text-gray-600">Calcul automatique:</p>
                      <p className="text-lg font-bold text-green-700">
                        Or restant: {(parseFloat(ordreEnCours.poids_or_donne || 0) - parseFloat(formTerminer.poids_or_utilise || 0)).toFixed(2)}g
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-500">
                  Cette etape ne necessite pas de saisie de poids or.
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold mb-1 flex items-center gap-1 text-blue-700">
                  <Clock size={16} />
                  Temps passé (minutes)
                </label>
                <input
                  type="text"
                  value={formTerminer.temps_minutes || ''}
                  onChange={(e) => setFormTerminer({...formTerminer, temps_minutes: e.target.value.replace(',', '.')})}
                  className="w-full border-2 border-blue-300 rounded px-3 py-2 text-lg font-semibold focus:ring-2 focus:ring-blue-400"
                  placeholder="Ex: 120"
                />
                {formTerminer.temps_minutes && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    = {(parseFloat(formTerminer.temps_minutes) / 60).toFixed(1)} heure(s)
                  </p>
                )}
              </div>

              {/* Poids bijou — systématique à chaque étape pour traçabilité perte métal */}
              <div>
                <label className="block text-sm font-semibold mb-1 flex items-center gap-1 text-purple-700">
                  <Weight size={16} />
                  Poids du bijou après cette étape (g)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  onFocus={e => e.target.select()}
                  value={formTerminer.poids_bijou_etape || ''}
                  onChange={(e) => setFormTerminer({...formTerminer, poids_bijou_etape: e.target.value.replace(',', '.')})}
                  className="w-full border-2 border-purple-300 rounded-lg px-3 py-2 text-lg font-semibold focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
                  placeholder="Peser et saisir le poids (g)"
                />
                <p className="text-xs text-purple-500 mt-1 font-medium">
                  ⚖️ Pesée systématique — permet de suivre la perte métal étape par étape
                </p>
              </div>

              {/* Rendre l'or au stock */}
              {formTerminer.poids_or_utilise && ordreEnCours?.poids_or_donne &&
                parseFloat(ordreEnCours.poids_or_donne) > parseFloat(formTerminer.poids_or_utilise) && (
                <label className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                  <input type="checkbox" checked={formTerminer.rendre_or || false}
                    onChange={e => setFormTerminer({...formTerminer, rendre_or: e.target.checked})}
                    className="w-4 h-4 rounded text-amber-600" />
                  <div>
                    <span className="text-sm font-medium text-amber-800">Rendre l'or non utilise au stock</span>
                    <span className="text-xs text-amber-600 ml-2">
                      ({(parseFloat(ordreEnCours.poids_or_donne) - parseFloat(formTerminer.poids_or_utilise)).toFixed(2)}g retour stock)
                    </span>
                  </div>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
                <textarea
                  value={formTerminer.notes}
                  onChange={(e) => setFormTerminer({...formTerminer, notes: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  placeholder="Remarques sur la fabrication..."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowTerminerModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400"
              >
                Annuler
              </button>
              <button
                onClick={terminerTache}
                className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 font-semibold"
              >
                ✓ Terminer la tâche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuiviOuvriersProduction;
